<script lang="ts">
  import type { Snippet } from 'svelte';
  import { describeCause, reportError } from '$lib/stores/errors.svelte';

  type Props = {
    label: string;
    tags: Record<string, string>;
    children: Snippet;
    scope?: 'page' | 'surface';
    retryLabel?: string;
  };

  let {
    label,
    tags,
    children,
    scope = 'surface',
    retryLabel = 'Try again',
  }: Props = $props();

  function onerror(error: unknown): void {
    reportError(label, error, { ...tags, source: 'svelte.boundary' });
  }

  function message(error: unknown): string {
    return describeCause(error).message;
  }
</script>

<svelte:boundary {onerror}>
  {@render children()}

  {#snippet failed(error, reset)}
    <section class="error-boundary" class:error-boundary--page={scope === 'page'} role="alert">
      <h2>Unable to render this view</h2>
      <p>{message(error)}</p>
      {#if scope === 'page'}
        <p class="error-boundary__hint">Try rendering the application again.</p>
      {:else}
        <p class="error-boundary__hint">Your other open work remains available. Try rendering this view again.</p>
      {/if}
      <button type="button" onclick={reset}>{retryLabel}</button>
    </section>
  {/snippet}
</svelte:boundary>

<style>
  .error-boundary {
    display: flex;
    flex: 1;
    min-height: 0;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--sp-2);
    padding: var(--sp-6);
    background: var(--bg-primary);
    color: var(--text-primary);
    text-align: center;
  }
  .error-boundary--page {
    min-height: 100vh;
  }
  .error-boundary h2 {
    margin: 0;
    color: var(--accent-error);
    font-size: var(--fs-lg);
  }
  .error-boundary p {
    max-width: 52ch;
    margin: 0;
  }
  .error-boundary__hint {
    color: var(--text-muted);
    font-size: var(--fs-sm);
  }
  .error-boundary button {
    border: 1px solid var(--border-node);
    border-radius: var(--radius-sm);
    padding: var(--sp-1) var(--sp-3);
    background: var(--bg-secondary);
    color: var(--text-primary);
    cursor: pointer;
  }
  .error-boundary button:hover {
    border-color: var(--accent-error);
  }
</style>
